import { PaymentRequestDetails, PaymentType } from '@/sbgl/payments';
import { assign, createMachine } from 'xstate';
import { log, sendParent } from 'xstate/lib/actions';
import { useApiStore } from '../../../stores/api';
import { ApiService } from '../api';
import { PaymentValidationError } from '../errors';

/**
 * Shared state container (context) maintained throughout the machine's lifecycle.
 * Holds credentials, tokens, payment metadata, and 3D Secure authentication details.
 */
export interface ApiContext {
  brandKey?: string;

  apiService: ApiService;

  paymentType: PaymentType;
  paymentRequestData?: PaymentRequestDetails;

  transactionKey?: string;
  paymentProvider?: string;
  token?: string;
  email?: string;
  name?: string;
  walletType?: 'google-pay' | 'apple-pay';

  /**
   * Holds 3D Secure v2 authentication payload returned by Global Payments / Card Issuer.
   */
  threeDSecureData?: {
    serverTransactionId?: string;
    authentication_value?: string;
    eci?: string;
    message_version?: string;
    acsTransactionId?: string;
    cres?: string;
    transStatus?: string;
  };
}

export type ApiEvent =
  // Kick off a new payment session with payload details
  | {
    type: 'GP_SETUP_NEW_PAYMENT';
    paymentRequestData: PaymentRequestDetails;
  }
  // Payment request validation outcome events
  | {
    type: 'GP_REQUEST_DETAILS_VALID';
    validated: object;
  }
  | {
    type: 'GP_REQUEST_DETAILS_INVALID';
    error: PaymentValidationError;
  }
  // Setup / Initialization events
  | {
    type: 'GP_API_SERVICE_LOADED';
    apiService: ApiService;
  }
  | {
    type: 'GP_ELEMENTS_LOADED';
    provider: string;
  }
  | {
    type: 'GP_FORM_READY';
  }
  // Triggered as soon as form submits to display UI loading/spinner state immediately
  | {
    type: 'GP_SUBMITTING_CARD_DETAILS';
  }
  // 3DS / Challenge interaction triggers
  | {
    type: 'GP_PAYMENT_REQUIRES_ACTION';
    transactionKey: string;
  }
  // Final payment status events
  | {
    type: 'GP_PAYMENT_SUCCEEDED';
    transactionKey: string;
  }
  | {
    type: 'GP_PAYMENT_FAILED';
    transactionKey?: string;
    error?: Error;
  }
  // Tokenization events (Card, Google Pay, Apple Pay)
  | {
      type: 'GP_TOKEN_RECEIVED';
      token: string;
      email: string;
      name: string;
      walletType?: 'google-pay' | 'apple-pay';
      serverTransactionId?: string;
      authentication_value?: string;
      eci?: string;
      message_version?: string;
      acsTransactionId?: string;
      transactionKey?: string;
    }
  | {
    type: 'GP_GOOGLE_PAY_TOKEN_RECEIVED';
    token: string;
    email?: string;
    name?: string;
    walletType?: 'google-pay';
  }
  | {
    type: 'GP_APPLE_PAY_TOKEN_RECEIVED';
    token: string;
    email?: string;
    name?: string;
    walletType?: 'apple-pay';
  }
  // Triggered when 3DS iframe or redirect challenge returns to the browser
  | {
    type: 'GP_3DS_CHALLENGE_COMPLETE';
    token: string;
    serverTransactionId: string;
    transStatus: string;
    name: string;
    email: string;
    cres?: string;
    authentication_value?: string;
    cavv?: string;
    eci?: string;
    message_version?: string;
    acsTransactionId?: string;
    transactionKey? :string;
  };

/**
 * An XState state machine describing the SBGL payments API capture flow.
 *
 * @see https://xstate.js.org/docs/
 */
export const PaymentMachine = createMachine<ApiContext, ApiEvent>(
  {
    id: 'gp',
    initial: 'loading',
    predictableActionArguments: true,
    strict: true,

    context: {
      brandKey: undefined,
      apiService: undefined,
      paymentType: undefined,
      paymentRequestData: undefined,
      transactionKey: undefined,
      threeDSecureData: undefined,
    },

    states: {
      // Setting up Global Payments SDK and SBG payments API service...
      loading: {
        always: [
          // Move to next state when both have been set up, proceed automatically to 'ready'
          {
            target: 'ready',
            cond: (context) => {
              const isValid = !!context.apiService;
              return isValid;
            },
          },
        ],
        on: {
          GP_API_SERVICE_LOADED: {
            actions: [
              assign({
                apiService: (context, event) => event.apiService,
              }),
            ],
          },
          GP_PAYMENT_FAILED: {
            target: 'failure',
            actions: [
              log(() => `Could not setup Global Payments API service`),
              sendParent((context, event) => ({
                type: 'PAYMENT_ERROR',
                error: event.error,
              })),
            ],
          },
        },
        invoke: [
          // Set up the SBG payments API service class.
          {
            src: (context, event) => (send, onReceive) => {
              const { brandKey } = context;
              const { apiBaseUrl } = useApiStore();

              try {
                const apiService = new ApiService(apiBaseUrl, brandKey);
                send({ type: 'GP_API_SERVICE_LOADED', apiService });
              } catch (error) {
                send({ type: 'GP_PAYMENT_FAILED', error });
              }
            },
          },
          // Verifies Global Payments window script presence
          {
            src: (context, event) => (send, onReceive) => {
              if (typeof window.GlobalPayments === 'undefined') {
                send({
                  type: 'GP_PAYMENT_FAILED',
                  error: new Error('GlobalPayments SDK not loaded'),
                });
                return;
              }
            },
          },
        ],
      },

      // Ready to start or resume a payment...
      ready: {
        entry: [
          sendParent({ type: 'INIT_API_READY' })
        ],
        on: {
          GP_SETUP_NEW_PAYMENT: {
            target: 'validatingRequestDetails',
            actions: [
              log(() => `Setting up new payment...`),
              assign((context, event) => {
                const { paymentRequestData } = event;
                const { paymentType } = paymentRequestData;

                return {
                  paymentType,
                  paymentRequestData,
                };
              }),
            ],
          },
        },
      },

      /**
       * Validates payload details against backend logic before displaying payment UI to user.
       */
      validatingRequestDetails: {
        tags: ['loading'],
        entry: [
          sendParent(() => ({ type: 'PAYMENT_PROCESSING' }))
        ],
        on: {
          GP_REQUEST_DETAILS_VALID: {
            target: 'handlingUserInput',
          },
          GP_REQUEST_DETAILS_INVALID: {
            target: 'failure',
            actions: [
              log(() => `Payment request details are not valid.`),
              sendParent((context, event) => ({
                type: 'PAYMENT_ERROR',
                error: event.error,
              })),
            ],
          },
          GP_PAYMENT_FAILED: {
            target: 'failure',
            actions: [
              log(() => `Could not validate payment request.`),
              sendParent((context, event) => ({
                type: 'PAYMENT_ERROR',
                error: event.error,
              })),
            ],
          },
        },
        invoke: {
          src: (context) => (send) => {
            const { apiService, paymentRequestData } = context;
            const request = apiService.checkPaymentFields(paymentRequestData);

            request
              .then((data) => {
                const { validated } = data;
                send({ type: 'GP_REQUEST_DETAILS_VALID', validated });
              })
              .catch((error) => {
                if (error instanceof PaymentValidationError) {
                  send({ type: 'GP_REQUEST_DETAILS_INVALID', error });
                  return;
                }
                send({ type: 'GP_PAYMENT_FAILED', error });
              });
          },
        },
      },

      /**
       * Form mounted state. Receives user card input/tokens or wallet tokens (Apple/Google Pay)
       * and routes them to the authorization step. Also handles client-side 3DS responses.
       */
      handlingUserInput: {
        entry: [
          sendParent(() => ({ type: 'PAYMENT_FORM_LOADING' }))
        ],
        on: {
          GP_SUBMITTING_CARD_DETAILS: {
            target: 'submittingCardDetails',
          },
          GP_ELEMENTS_LOADED: {
            actions: [
              assign({
                paymentProvider: (context, event) => event.provider,
              }),
            ],
          },
          GP_FORM_READY: {
            actions: [
              sendParent(() => ({ type: 'PAYMENT_FORM_READY' }))
            ],
          },
          GP_PAYMENT_REQUIRES_ACTION: {
            target: 'handleClientInteraction',
            actions: [
              assign({
                transactionKey: (context, event) => event.transactionKey,
              }),
            ],
          },
          GP_TOKEN_RECEIVED: {
            target: 'authorisingPayment',
            actions: [
              assign({
                token: (context, event) => event.token,
                email: (context, event) => event.email,
                name: (context, event) => event.name || context.name || context.paymentRequestData?.fields.name,
                walletType: (context, event) => event.walletType,
                transactionKey: (context, event) => event.transactionKey || context.transactionKey,
                threeDSecureData: (context, event) => ({
                  serverTransactionId: event.serverTransactionId,
                  authentication_value: event.authentication_value,
                  eci: event.eci,
                  message_version: event.message_version,
                  acsTransactionId: event.acsTransactionId,
                }),
              }),
            ],
          },
          GP_GOOGLE_PAY_TOKEN_RECEIVED: {
            target: 'authorisingPayment',
            actions: [
              assign({
                token: (context, event) => event.token,
                email: (context, event) => event.email || context.email,
                name: (context, event) => event.name,
                walletType: () => 'google-pay',
              }),
            ],
          },
          GP_APPLE_PAY_TOKEN_RECEIVED: {
            target: 'authorisingPayment',
            actions: [
              assign({
                token: (context, event) => event.token,
                email: (context, event) => event.email || context.email,
                name: (context, event) => event.name,
                walletType: () => 'apple-pay',
              }),
            ],
          },
        },
      },

      submittingCardDetails: {
        tags: ['loading'],
        entry: [
          sendParent(() => ({ type: 'PAYMENT_PROCESSING' }))
        ],
        on: {
          GP_TOKEN_RECEIVED: {
            target: 'authorisingPayment',
            actions: [
              assign({
                token: (context, event) => event.token,
                email: (context, event) => event.email,
                name: (context, event) => event.name || context.name || context.paymentRequestData?.fields.name,
                walletType: (context, event) => event.walletType,
                transactionKey: (context, event) => event.transactionKey || context.transactionKey,
                threeDSecureData: (context, event) => ({
                  serverTransactionId: event.serverTransactionId,
                  authentication_value: event.authentication_value,
                  eci: event.eci,
                  message_version: event.message_version,
                  acsTransactionId: event.acsTransactionId,
                }),
              }),
            ],
          },
          GP_PAYMENT_REQUIRES_ACTION: {
            target: 'handleClientInteraction',
            actions: [
              assign({
                transactionKey: (context, event) => event.transactionKey,
              }),
            ],
          },
          GP_3DS_CHALLENGE_COMPLETE: {
            target: 'authorisingPayment',
            actions: [
              assign({
                email: (context, event) => event.email,
                name: (context, event) => event.name || context.name || context.paymentRequestData?.fields.name,
                token: (context, event) => event.token,
                transactionKey: (context, event) => event.transactionKey || context.transactionKey,
                threeDSecureData: (context, event) => ({
                  serverTransactionId: event.serverTransactionId,
                  authentication_value: event.authentication_value,
                  eci: event.eci,
                  message_version: event.message_version,
                  acsTransactionId: event.acsTransactionId,
                  cavv: event.cavv,
                }),
              }),
            ],
          },
          GP_PAYMENT_FAILED: {
            target: 'failure',
            actions: [
              log(() => `Could not verify or submit card details.`),
              sendParent((context, event) => ({
                type: 'PAYMENT_ERROR',
                error: event.error,
              })),
            ],
          },
        },
      },

      /**
       * Submits the payment token and initial 3DS details to the backend API.
       * Evaluates response to check if payment finishes or requires a 3DS Challenge.
       */
      authorisingPayment: {
        tags: ['loading'],
        entry: [
          sendParent(() => ({ type: 'PAYMENT_PROCESSING' }))
        ],
        invoke: {
          src: async (context, event) => {

            const {
              apiService,
              token,
              paymentRequestData,
              name,
              email,
              walletType,
              threeDSecureData,
            } = context;

            return await apiService.submitPayment(
              paymentRequestData,
              token,
              name,                    
              email,                   
              walletType,              
              threeDSecureData?.serverTransactionId,
              threeDSecureData?.authentication_value,
              threeDSecureData?.eci,
              threeDSecureData?.message_version,
              threeDSecureData?.acsTransactionId
            );
          },
          onDone: [
            {
              // If backend responds that a 3DS challenge is needed, transition to handle client interaction
              target: 'handleClientInteraction',
              cond: (context, event) => {
                const data = event.data;
                const requiresAction = Boolean(
                  data.requiresAction ||
                  data.status === 'CHALLENGE_REQUIRED' ||
                  data.threeDSecureData ||
                  data.acsTransactionId
                );

                return requiresAction;
              },
              actions: [

                assign({
                  transactionKey: (context, event) => {
                    const resolvedKey = (
                      event.data?.transactionKey || 
                      event.data?.transaction_key || 
                      event.data?.data?.transactionKey || 
                      context.transactionKey
                    );
                    return resolvedKey;
                  },
                  threeDSecureData: (context, event) => ({
                    ...context.threeDSecureData,
                    ...(event.data.threeDSecureData || event.data),
                    acsTransactionId:
                      event.data.acsTransactionId ||
                      event.data.threeDSecureData?.acsTransactionId,
                    serverTransactionId:
                      event.data.serverTransactionId ||
                      event.data.threeDSecureData?.serverTransactionId,
                  }),
                }),
                sendParent(() => ({ type: 'PAYMENT_REQUIRES_ACTION' })),
              ],
            },
            {
              // Frictionless flow: proceed directly to payment capture
              target: 'attemptPaymentCapture',
              actions: [

                assign({
                  transactionKey: (context, event) => {
                    return (
                      event.data?.transactionKey || 
                      event.data?.transaction_key || 
                      event.data?.paymenttransactionKey ||
                      event.data?.payment_transaction_key ||
                      event.data?.data?.transactionKey || 
                      context.transactionKey
                    );
                  },
                }),
              ],
            },
          ],
          onError: {
            target: 'failure',
            actions: [

              sendParent((context, event) => ({
                type: 'PAYMENT_FAILED',
                error: event.data,
              })),
            ],
          },
        },
      },

      /**
       * Finalizes payment processing on the backend post-authentication using saved session data.
       */
      attemptPaymentCapture: {
        tags: ['loading'],
        entry: [
          sendParent(() => ({ type: 'PAYMENT_PROCESSING' }))
        ],
        on: {
          GP_PAYMENT_SUCCEEDED: {
            target: 'success',
            actions: [
              log(() => `Completed payment.`),
              assign({
                transactionKey: (context, event) =>
                  event.transactionKey || context.transactionKey,
              }),
            ],
          },
          GP_PAYMENT_REQUIRES_ACTION: {
            target: 'handleClientInteraction',
            actions: [
              log(() => `Payment requires action.`),
              assign({
                transactionKey: (context, event) =>
                  event.transactionKey || context.transactionKey,
              }),
            ],
          },
          GP_PAYMENT_FAILED: {
            target: 'failure',
            actions: [
              log(() => `Could not complete payment.`),
              sendParent((context, event) => ({
                type: 'PAYMENT_ERROR',
                error: event.error,
              })),
            ],
          },
        },

        invoke: {
          src: (context) => (send) => {
            const {
              apiService,
              paymentType,
              transactionKey,
              token,
              threeDSecureData,
            } = context;


            const payload = {
              ...threeDSecureData,
            };

            const request = apiService.continueExistingPayment(
              paymentType,
              token,
              transactionKey,
              payload
            );

            request
              .then((data) => {
                const { success, requiresAction } = data;
                if (success) {
                  return send({ type: 'GP_PAYMENT_SUCCEEDED', transactionKey });
                } else if (requiresAction) {
                  return send({
                    type: 'GP_PAYMENT_REQUIRES_ACTION',
                    transactionKey,
                  });
                } else {
                  return send({ type: 'GP_PAYMENT_FAILED', transactionKey });
                }
              })
              .catch((error) => {
                send({ type: 'GP_PAYMENT_FAILED', transactionKey, error });
              });
          },
        },
      },

      /**
       * Waiting state while 3DS challenge iframe/popup is active on screen.
       * Awaits completion or failure events from the iframe listener.
       */
      handleClientInteraction: {
        entry: [
          log(() => `Awaiting 3DS challenge completion...`)
        ],
        on: {
          GP_3DS_CHALLENGE_COMPLETE: [
            // If 3DS failed/declined during challenge, fail immediately
            {
              target: 'failure',
              cond: (context, event) => {
                const isFailed = ['N', 'R', 'U'].includes(event.transStatus);

                return isFailed;
              },
              actions: [
                log(
                  () => `3DS Challenge declined by issuer`
                ),
                assign({
                  threeDSecureData: (context, event) => ({
                    ...context.threeDSecureData,
                    serverTransactionId: event.serverTransactionId,
                    cres: event.cres || context.threeDSecureData?.cres,
                    transStatus: event.transStatus,
                  }),
                }),
                sendParent((context, event) => ({
                  type: 'PAYMENT_FAILED',
                  error: new Error(
                    '3D Secure authentication was declined by your card issuer.'
                  ),
                  transactionKey: context.transactionKey,
                })),
              ],
            },
            {
              target: 'authorisingPayment',
              cond: (context, event) => {
                const isSuccess = ['Y', 'A'].includes(event.transStatus);

                return isSuccess;
              },
              actions: [
                assign({
                  token: (context, event) => event.token || context.token,
                  name: (context, event) => event.name || context.name,
                  email: (context, event) => event.email || context.email,
                  transactionKey: (context, event) => event.transactionKey || context.transactionKey,
                  threeDSecureData: (context, event) => ({
                    ...context.threeDSecureData,
                    serverTransactionId: event.serverTransactionId,
                    cres: event.cres || context.threeDSecureData?.cres,
                    transStatus: event.transStatus,
                    eci: event.eci || context.threeDSecureData?.eci,
                    authentication_value:
                      event.authentication_value ||
                      event.cavv ||
                      context.threeDSecureData?.authentication_value,
                    message_version: event.message_version || context.threeDSecureData?.message_version,
                    acsTransactionId: event.acsTransactionId || context.threeDSecureData?.acsTransactionId,
                  }),
                }),
              ],
            },
          ],
          GP_PAYMENT_FAILED: {
            target: 'failure',
            actions: [

              log(() => `3DS Challenge failed or cancelled.`),
              sendParent((context, event) => ({
                type: 'PAYMENT_FAILED',
                error: event.error || new Error('3D Secure challenge failed.'),
                transactionKey: context.transactionKey,
              })),
            ],
          },
        },
      },

      /**
       * Notifies parent actor of successful completion.
       */
      success: {
        type: 'final',
        entry: [
          sendParent((context) => ({
            type: 'PAYMENT_SUCCEEDED',
            transactionKey: context.transactionKey,
          })),
        ],
      },

      /**
       * Notifies parent actor of failed transaction.
       */
      failure: {
        type: 'final',
        entry: [
          sendParent((context) => ({
            type: 'PAYMENT_FAILED',
            transactionKey: context.transactionKey,
          })),
        ],
      },
    },
  },
  {
    guards: {},
    services: {},
  }
);